/**
 * MDTable Toolbar Component
 *
 * Purpose: Custom toolbar for MDTable with built-in controls and custom filter support
 * Architecture: React component using MUI DataGrid toolbar components
 * Dependencies: MUI DataGrid toolbar components, MUI Box
 * Author: Development Team
 * Created: 2025-01-27 (Extracted from MDTable.tsx during refactoring)
 */

import React from 'react';
import { Box } from '@mui/material';
import {
  QuickFilter,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from '@mui/x-data-grid-pro';

/**
 * Custom toolbar component that includes custom filter controls
 * Updated for MUI DataGrid v8 - uses Box container with individual toolbar components
 */
export interface MDTableToolbarProps {
  showQuickFilter?: boolean;
  customFilters?: React.ReactNode;
  customToolbarActions?: React.ReactNode;
  /**
   * Hides the built-in filter button. Used when the table is in row-reorder
   * mode, where grid-internal filtering is disabled so drag indices stay
   * valid against the unfiltered row order.
   */
  hideFilterButton?: boolean;
}

const MDTableToolbar: React.FC<MDTableToolbarProps> = ({
  showQuickFilter = false,
  customFilters,
  customToolbarActions,
  hideFilterButton = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        px: 1,
        py: 0.5,
        minHeight: 52, // Match DataGrid toolbar height
      }}
    >
      {/* Left side - Built-in toolbar controls + custom filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <GridToolbarColumnsButton />
        {!hideFilterButton && <GridToolbarFilterButton />}
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        {showQuickFilter && <QuickFilter />}

        {/* Custom filters positioned after built-in controls */}
        {customFilters && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{customFilters}</Box>
        )}
      </Box>

      {/* Right side - Custom actions */}
      {customToolbarActions && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{customToolbarActions}</Box>
      )}
    </Box>
  );
};

export default MDTableToolbar;
